import { useState } from 'react';
import { useStandardTerms, StandardTerm } from '@/hooks/useStandardTerms';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, FileText, ChevronDown, ChevronUp, Loader2, Edit2, Trash2, Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { RichTextEditor } from './RichTextEditor';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface StandardTermsSelectorProps {
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  className?: string;
}

export const StandardTermsSelector = ({ 
  selectedIds, 
  onChange, 
  className 
}: StandardTermsSelectorProps) => {
  const { terms, loading, createTerm, updateTerm, deleteTerm, isCreating, isUpdating } = useStandardTerms();
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTerm, setNewTerm] = useState({ name: '', content: '' });
  const [editingTerm, setEditingTerm] = useState<StandardTerm | null>(null);
  const [editContent, setEditContent] = useState('');
  const [editName, setEditName] = useState('');
  const [deleteTermId, setDeleteTermId] = useState<string | null>(null);
  const [expandedTermId, setExpandedTermId] = useState<string | null>(null);

  const handleToggle = (termId: string) => {
    if (selectedIds.includes(termId)) {
      onChange(selectedIds.filter(id => id !== termId));
    } else {
      onChange([...selectedIds, termId]);
    }
  };

  const handleAddTerm = async () => {
    if (!newTerm.name.trim()) return;
    
    const created = await createTerm({
      name: newTerm.name,
      content: newTerm.content || null,
      is_default: false,
    });
    
    // Auto-select the newly created term
    if (created?.id) {
      onChange([...selectedIds, created.id]);
    }
    
    setNewTerm({ name: '', content: '' });
    setShowAddForm(false);
  };

  const handleStartEdit = (term: StandardTerm, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingTerm(term);
    setEditName(term.name);
    setEditContent(term.content || '');
  };

  const handleSaveEdit = async () => {
    if (!editingTerm || !editName.trim()) return;
    
    await updateTerm({
      id: editingTerm.id,
      name: editName,
      content: editContent || null,
    });
    
    setEditingTerm(null);
    setEditName('');
    setEditContent('');
  };

  const handleCancelEdit = () => {
    setEditingTerm(null);
    setEditName('');
    setEditContent('');
  };

  const handleDeleteTerm = async () => {
    if (!deleteTermId) return;
    
    await deleteTerm(deleteTermId);
    
    // Remove from selection if selected
    if (selectedIds.includes(deleteTermId)) {
      onChange(selectedIds.filter(id => id !== deleteTermId));
    }
    
    setDeleteTermId(null);
  };

  const handleExpandTerm = (termId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedTermId(expandedTermId === termId ? null : termId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {selectedIds.length} geselecteerd
        </span>
      </div>
      
      {/* Terms list */}
      <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
        {terms.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">
            Nog geen standaard voorwaarden aangemaakt
          </p>
        ) : (
          terms.map((term) => {
            const isEditing = editingTerm?.id === term.id;
            const isExpanded = expandedTermId === term.id;
            
            if (isEditing) {
              return (
                <div key={term.id} className="p-4 rounded-lg border-2 border-primary bg-primary/5 space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Naam</Label>
                    <Input
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder="Naam van de voorwaarden"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Inhoud (ondersteunt opmaak)</Label>
                    <RichTextEditor
                      value={editContent}
                      onChange={setEditContent}
                      placeholder="Typ of plak de voorwaarden hier..."
                      minHeight="300px"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCancelEdit}>
                      <X className="h-4 w-4 mr-1" />
                      Annuleren
                    </Button>
                    <Button size="sm" onClick={handleSaveEdit} disabled={isUpdating || !editName.trim()}>
                      {isUpdating ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4 mr-1" />
                      )}
                      Opslaan
                    </Button>
                  </div>
                </div>
              );
            }
            
            return (
              <div
                key={term.id}
                className={cn(
                  "rounded-lg border transition-colors",
                  selectedIds.includes(term.id)
                    ? "border-primary bg-primary/5"
                    : "border-border hover:bg-muted/50"
                )}
              >
                <div 
                  className="flex items-start gap-3 p-3 cursor-pointer"
                  onClick={() => handleToggle(term.id)}
                >
                  <Checkbox
                    checked={selectedIds.includes(term.id)}
                    onCheckedChange={() => handleToggle(term.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm">{term.name}</span>
                      {term.is_default && (
                        <span className="text-[10px] bg-primary/10 text-primary px-1.5 py-0.5 rounded shrink-0">
                          Standaard
                        </span>
                      )}
                    </div>
                    {term.description && (
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {term.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => handleExpandTerm(term.id, e)}
                      title="Inhoud bekijken"
                    >
                      {isExpanded ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={(e) => handleStartEdit(term, e)}
                      title="Bewerken"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); setDeleteTermId(term.id); }}
                      title="Verwijderen"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
                
                {isExpanded && term.content && (
                  <div className="px-3 pb-3 pt-0">
                    <div 
                      className="p-3 bg-muted/50 rounded text-sm prose prose-sm max-w-none max-h-[300px] overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: term.content }}
                    />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
      
      {/* Add new term section */}
      <Collapsible open={showAddForm} onOpenChange={setShowAddForm}>
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start text-muted-foreground hover:text-foreground"
          >
            {showAddForm ? (
              <ChevronUp className="h-4 w-4 mr-2" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Nieuwe standaard voorwaarden toevoegen
          </Button>
        </CollapsibleTrigger>
        
        <CollapsibleContent className="pt-3 space-y-3">
          <div className="p-4 border rounded-lg bg-muted/30 space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="term-name" className="text-xs">Naam</Label>
              <Input
                id="term-name"
                placeholder="bijv. Algemene Voorwaarden 2026"
                value={newTerm.name}
                onChange={(e) => setNewTerm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="term-content" className="text-xs">Inhoud (ondersteunt opmaak)</Label>
              <RichTextEditor
                value={newTerm.content}
                onChange={(content) => setNewTerm(prev => ({ ...prev, content }))}
                placeholder="Typ of plak de voorwaarden hier... Je kunt tekst vanuit Word plakken."
                minHeight="200px"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowAddForm(false);
                  setNewTerm({ name: '', content: '' });
                }}
              >
                Annuleren
              </Button>
              <Button
                size="sm"
                onClick={handleAddTerm}
                disabled={!newTerm.name.trim() || isCreating}
              >
                {isCreating ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                Toevoegen
              </Button>
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteTermId} onOpenChange={() => setDeleteTermId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Voorwaarden verwijderen</AlertDialogTitle>
            <AlertDialogDescription>
              Weet je zeker dat je deze standaard voorwaarden wilt verwijderen? 
              Dit kan niet ongedaan worden gemaakt.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuleren</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteTerm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Verwijderen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
